import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import {
  addFolderIcon,
  filterIcon,
  menuDeleteIcon,
  menuEditIcon,
  viewIcon,
} from "../../assets/icons";
import {
  Card,
  EmptyLayout,
  Pagination,
  SearchBar,
} from "../../components/molecules";
import { DRAWERS } from "../../components/organisms/AppDrawers/drawers.constants";
import DeleteConfirmationModal from "../../components/organisms/AppModals/DeleteConfirmationModal";
import { MODALS } from "../../components/organisms/AppModals/modals.constants";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import { setSelectedDrawer } from "../../components/organisms/Drawer";
import { actions as modalActions } from "../../components/organisms/Modal/modal.reducers";
import {
  POPOVER_BUTTONS,
  BADGE_VARIANTS,
  DEFAULT_LIMIT,
  EMPTY_ARRAY,
  ONE,
} from "../../constants";
import { selectOrganizations, selectOrganizationsFilters } from "./index";
import {
  deleteOrganization,
  fetchOrganizations,
} from "./organizations.actions";
import { actions as organizationActions } from "./organizations.reducers";
import { actions as appActions } from "../Applications/applications.reducers";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { selectSelectedModal } from "../../components/organisms/Modal";
import FilterOrganizationsModal from "../../components/organisms/AppModals/FilterOrganizationsModal";

const { DELETE_MODAL, FILTER_ORGANIZATIONS_MODAL } = MODALS;
const { MANAGE_ORGANIZATION_DRAWER } = DRAWERS;
const { DELETE, VIEW, EDIT } = POPOVER_BUTTONS;
const {
  setSelectedOrganizationId,
  setOrganizationsFilters,
  setSelectedOrganization,
} = organizationActions;
const { setSelectedModal, setItemToBeDeleted } = modalActions;
const { setSelectedTab } = breadCrumbActions;
const { setShouldShowLeftPane } = appActions;
const { PRIMARY, SUCCESS } = BADGE_VARIANTS;
const { FETCH_ORGANIZATIONS } = SPINNER_NAMES;
const TWO = 2;

const Organizations = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const scrollRef = useRef();

  const organizationsFilters = useSelector(selectOrganizationsFilters);
  const { data: fetchedOrganizations, count: organizationsCount, total: totalOrganizations } =
    useSelector(selectOrganizations);
  const selectedModal = useSelector(selectSelectedModal);

  const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState(false);
  const [pageCount, setPageCount] = useState(ONE);
  const [organizations, setOrganizations] = useState(EMPTY_ARRAY);
  const [shouldRenderFilterModal, setShouldRenderFilterModal] = useState(false);

  useEffect(() => {
    dispatch(setShouldShowLeftPane(false));
    dispatch(setSelectedTab(null));
    return () => {
      dispatch(setShouldShowLeftPane(true));
      dispatch(setOrganizationsFilters());
    };
  }, [dispatch]);

  useEffect(() => {
    if (fetchedOrganizations) setOrganizations(fetchedOrganizations);
  }, [fetchedOrganizations]);

  useEffect(() => {
    scrollRef.current.scrollIntoView({ behavior: "smooth" });
  }, [organizationsFilters, organizations]);

  useEffect(() => {
    setPageCount(Math.ceil(organizationsCount / DEFAULT_LIMIT));
  }, [organizationsCount]);

  const popOverOptions = (organization) => [
    {
      name: VIEW,
      icon: viewIcon,
      handleOnClick: () => {
        navigate(`/organizations/${organization.id}/users`);
        dispatch(setSelectedOrganization(organization));
      },
    },
    {
      name: DELETE,
      icon: menuDeleteIcon,
      classes: "border-t border-gray-600",
      handleOnClick: () => {
        dispatch(setSelectedModal(DELETE_MODAL));
        dispatch(setItemToBeDeleted(organization));
        setShouldShowDeleteModal(true);
      },
    },
    {
      name: EDIT,
      icon: menuEditIcon,
      classes: "border-t border-gray-600",
      handleOnClick: () => {
        dispatch(setSelectedOrganizationId(organization.id));
        dispatch(setSelectedOrganization(organization));
        dispatch(setSelectedDrawer(MANAGE_ORGANIZATION_DRAWER));
      },
    },
  ];

  const handleDeleteOrganization = useCallback(
    (organization) => {
      dispatch(deleteOrganization(organization.id)).then((res) => {
        if (res.payload) {
          setShouldShowDeleteModal(false);
          dispatch(fetchOrganizations(organizationsFilters));
        }
      });
    },
    [dispatch, organizationsFilters]
  );

  const getUsersCount = (organization) =>
  organization?.userOrganizationAssociations?.length < TWO
  ? `${organization?.userOrganizationAssociations?.length} user`
  : `${organization?.userOrganizationAssociations?.length} users`;

  const getBadges = (organization) => [
    {
      color: SUCCESS,
      value: organization.subscriptions?.name,
    },
    {
      color: PRIMARY,
      value: getUsersCount(organization),
    },
  ];

  const handleOnSearch = useCallback(
    (value) =>
      dispatch(
        setOrganizationsFilters({
          ...organizationsFilters,
          name: value,
          page: ONE,
        })
      ),
    [dispatch, organizationsFilters]
  );

  const handlePagination = useCallback(
    (value) => {
      dispatch(
        setOrganizationsFilters({
          ...organizationsFilters,
          page: value + ONE,
        })
      );
    },
    [dispatch, organizationsFilters]
  );

  const renderOrganizations = () =>
    !isEmpty(organizations) ? (
      <div className="grid grid-cols-12 gap-4 w-full">
        {organizations.map((organization) => (
          <Card
            key={organization.id}
            name={organization.name}
            createdAt={organization.createdAt}
            badges={getBadges(organization)}
            popOverOptions={popOverOptions(organization)}
            onClick={() => {
              navigate(`/organizations/${organization.id}/users`);
              dispatch(setSelectedOrganization(organization));
            }}
          />
        ))}

        {shouldShowDeleteModal && (
          <DeleteConfirmationModal
            handleDelete={handleDeleteOrganization}
            setShouldShowDeleteModal={setShouldShowDeleteModal}
            title="delete organization"
          />
        )}
      </div>
    ) : (
      <EmptyLayout
        title="No Organizations Found"
        image={addFolderIcon}
        shouldShowButton={false}
      />
    );

  useEffect(() => {
    setShouldRenderFilterModal(selectedModal === FILTER_ORGANIZATIONS_MODAL);
  }, [selectedModal]);

  return (
    <div
      className="scrollbar content overflow-auto p-6 flex flex-1 flex-col gap-6 px-6 py-4"
      ref={scrollRef}
    >
      <Helmet>
        <title>Organizations | TavaJS</title>
      </Helmet>
      <div className="flex-col md:flex-row flex mb-4 gap-4 justify-between">
        {!!totalOrganizations && (
          <SearchBar placeholder="Search" handleOnSearch={handleOnSearch} />
        )}
        <button
          className="btn btn-primary-light ml-1  flex  mr-2"
          type="submit"
          onClick={() => dispatch(setSelectedModal(FILTER_ORGANIZATIONS_MODAL))}
        >
          <div className="icon mr-2">
            <img src={filterIcon} width="20" alt="plusLightIcon" />
          </div>
          Filter
        </button>
      </div>
      <Spinner name={FETCH_ORGANIZATIONS}>{renderOrganizations()}</Spinner>
      <Pagination
        handlePagination={handlePagination}
        pagesCount={pageCount}
        activePage={organizationsFilters.page - ONE}
        count={organizationsCount}
      />
      {shouldRenderFilterModal && <FilterOrganizationsModal />}
    </div>
  );
};
export default Organizations;
